import { ActivatedRouteSnapshot, Routes } from '@angular/router';
import { CustomersPage } from '../customers/customers.page';
import { UsersPage } from '../users/users.page';
import { inject } from '@angular/core';
import { CustomerService } from '../../../core/services/customer.service';
import { FormComponent, IFormResource } from '../../../shared/components/form/form.component';
import { SensorService } from '../../../core/services/sensors.service';
import { SettingsPage } from '../settings/settings.page';
import { StructurePage } from '../structure/structure.page';
import { FacilitiesService } from '../../../core/services/facilities.service';
import { UserService } from '../../../core/services/user.service';
import { DevicesPage } from '../devices/devices.page';
import { organizationGuard } from '../../../core/guards/organization.guard';
import { SideBarResolver } from '../../../core/services/resolvers/sideBar.resolver';
import { DeveloperSettingsPage } from '../developerSettings/developerSettings.page';
import { MentoragePage } from '../mentorage/mentorage.page';
import { AnalyticsPage } from '../analytics/analytics.page';
import { AlertsHistoryPage } from '../alertsHistory/alertsHistory.page';
import { SupportPage } from '../support/support.page';
import { DiagnosticComponent } from '../diagnostic/diagnostic.component';
import { LiveTelemetryPage } from '../live-telemetry/live-telemetry.component';
import { MentorageConfigPage } from '../mentorage-config/mentorage-config.page';
import { logsGuard } from '../../../core/guards/logs.guard';

export const RootRoutes: Routes = [
  {
    path: 'guide',
    loadComponent: () => import('../guide/guide.page').then(module => module.GuidePage),
    resolve: { sideBarResolver: SideBarResolver }
  },
  {
    path: 'logs',
    loadComponent: () => import('../logs/logs.page').then(module => module.LogsPage),
    canActivate: [logsGuard],
    resolve: { sideBarResolver: SideBarResolver }
  },
  /* Clienti */
  {
    path: "customers",
    children: [
      /* Pagina Clienti */
      {
        path: "",
        component: CustomersPage
      },
      /* Modifica cliente */
      {
        path: 'edit/:resourceId',
        component: FormComponent,
        resolve: {
          data: (route: ActivatedRouteSnapshot) => ({
            data: inject(CustomerService).getCustomer$(route.params['resourceId']),
            category: 'customer',
            breadcrumbs: [{
              icon: 'group',
              routerLink: '/wita/customers',
              label: 'CUSTOMERS'
            }, {
              label: 'EDIT_CUSTOMER',
              showBreadCrumbSubject: true
            }]
          } as IFormResource)
        }
      },
      /* Nuovo cliente */
      {
        path: 'new',
        component: FormComponent,
        resolve: {
          data: () => ({
            category: 'customer',
            breadcrumbs: [{
              icon: 'group',
              routerLink: '/wita/customers',
              label: 'CUSTOMERS'
            }, {
              label: 'NEW_CUSTOMER'
            }]
          })
        }
      },
      /* Nuova organizzazione per cliente */
      {
        path: 'new_org',
        component: FormComponent,
        resolve: {
          data: () => ({
            category: 'organization',
            breadcrumbs: [{
              icon: 'group',
              routerLink: '/wita/customers',
              label: 'CUSTOMERS'
            }, {
              label: 'NEW_ORGANIZATION'
            }]
          })
        }
      },
      /* Modifica organizzazione */
      {
        path: 'edit_org/:resourceId/:customerId',
        component: FormComponent,
        resolve: {
          data: (route: ActivatedRouteSnapshot) => ({
            data: inject(FacilitiesService).getOrganizationById$(route.params['resourceId']),
            category: 'organization',
            breadcrumbs: [{
              icon: 'group',
              routerLink: '/wita/customers',
              label: 'CUSTOMERS'
            }, {
              label: 'EDIT_ORGANIZATION'
            }]
          })
        }
      },
    ],
    resolve: {
      sideBarResolver: SideBarResolver
    }
  },
  /* Utenti */
  {
    path: "users",
    children: [
      /* Pagina Utenti */
      {
        path: '',
        component: UsersPage,
      },
      /* Nuovo utente */
      {
        path: 'new',
        component: FormComponent,
        resolve: {
          data: () => ({
            category: 'user',
            breadcrumbs: [{
              icon: 'group',
              routerLink: '/wita/users',
              label: 'USERS'
            }, {
              label: 'NEW_USER'
            }]
          })
        }
      },
      /* Modifica utente */
      {
        path: 'edit/:resourceId',
        component: FormComponent,
        resolve: {
          data: (route: ActivatedRouteSnapshot) => ({
            data: inject(UserService).getUser$(route.params['resourceId']),
            category: 'user',
            breadcrumbs: [{
              icon: 'groups',
              routerLink: '/wita/users',
              label: 'USERS'
            }, {
              label: 'EDIT_USER'
            }]
          })
        }
      }
    ],
    resolve: {
      sideBarResolver: SideBarResolver
    }
  },
  /* Struttura */
  {
    path: "structure",
    children: [
      /* Pagina Struttura */
      {
        path: "",
        component: StructurePage,
      },
      /* Modifica edificio */
      {
        path: 'edit_building/:buildingId',
        component: FormComponent,
        resolve: {
          data: (route: ActivatedRouteSnapshot) => ({
            data: inject(FacilitiesService).getBuilding$(route.params['buildingId']),
            category: 'building',
            breadcrumbs: [{
              icon: 'group',
              routerLink: '/wita/structure',
              label: 'STRUCTURE'
            }, {
              label: 'EDIT_BUILDING'
            }]
          })
        }
      },
      /* Nuovo edificio */
      {
        path: 'new_building',
        component: FormComponent,
        resolve: {
          data: () => ({
            category: 'building',
            breadcrumbs: [{
              icon: 'group',
              routerLink: '/wita/structure',
              label: 'STRUCTURE'
            }, {
              label: 'NEW_BUILDING'
            }]
          })
        }
      },
      /* Nuovo reparto */
      {
        path: 'new_ward',
        component: FormComponent,
        resolve: {
          data: () => ({
            category: 'structure',
            breadcrumbs: [{
              icon: 'group',
              routerLink: '/wita/structure',
              label: 'STRUCTURE'
            }, {
              label: 'NEW_WARD'
            }]
          })
        }
      },
      /* Modifica reparto */
      {
        path: 'edit_ward/:unitId',
        component: FormComponent,
        resolve: {
          data: (route: ActivatedRouteSnapshot) => ({
            data: inject(FacilitiesService).getWardByIdWithRoomsAndBeds$(route.params['unitId']),
            category: 'structure',
            breadcrumbs: [{
              icon: 'group',
              routerLink: '/wita/structure',
              label: 'STRUCTURE'
            }, {
              label: 'EDIT_WARD'
            }]
          })
        }
      }
    ],
    canActivate: [organizationGuard]
  },
  /* Sensori */
  {
    path: "devices",
    children: [
      /* Pagina sensori */
      {
        path: "",
        component: DevicesPage,
      },
      /* Modifica associazione dispositivo */
      {
        path: 'link/:resourceId',
        component: FormComponent,
        resolve: {
          data: (route: ActivatedRouteSnapshot) => ({
            data: inject(SensorService).getDevice$(route.params['resourceId']),
            category: 'deviceLink',
            breadcrumbs: [{
              icon: 'videocam',
              routerLink: '/wita/devices',
              label: 'DEVICES'
            }, {
              label: 'EDIT_LINK_DEVICE'
            }]
          })
        }
      },
      /* Associa dispositivo */
      {
        path: 'link',
        component: FormComponent,
        resolve: {
          data: () => ({
            category: 'deviceLink',
            breadcrumbs: [{
              icon: 'videocam',
              routerLink: '/wita/devices',
              label: 'DEVICES'
            }, {
              label: 'LINK_DEVICE'
            }]
          })
        }
      },
      /* Telemetrie Live */
      {
        path: 'liveTelemetry/:deviceLabel',
        component: LiveTelemetryPage,
        resolve: {
          data: () => ({
            category: 'deviceLink',
            breadcrumbs: [{
              icon: 'videocam',
              routerLink: '/wita/devices',
              label: 'DEVICES'
            }, {
              label: 'LIVE_TELEMETRY'
            }]
          })
        }
      },
      {
        path: 'mentorageConfig/:deviceId',
        component: MentorageConfigPage,
        resolve: {
          data: () => ({
            breadcrumbs: [{
              icon: 'videocam',
              routerLink: '/wita/devices',
              label: 'DEVICES'
            }, {
              label: 'MENTORAGE_CONFIG'
            }]
          })
        }
      },
    ],
    resolve: {
      sideBarResolver: SideBarResolver
    }
  },
  /* Impostazioni */
  {
    path: 'settings',
    component: SettingsPage,
    resolve: {
      sideBarResolver: SideBarResolver
    }
  },
  /* Supporto */
  {
    path: 'support',
    component: SupportPage,
    resolve: {
      sideBarResolver: SideBarResolver
    }
  },
  /* Impostazioni Sviluppatore */
  {
    path: 'developerSettings',
    component: DeveloperSettingsPage,
    resolve: {
      SideBarResolver: SideBarResolver
    }
  },
  /* Mentorage */
  {
    path: 'mentorage',
    component: MentoragePage,
    resolve: {
      SideBarResolver: SideBarResolver
    },
    canActivate: [organizationGuard]
  },
  /* Statistiche */
  {
    path: 'analytics',
    component: AnalyticsPage,
    resolve: {
      SideBarResolver: SideBarResolver
    }
  },
  /* Diagnostica */
  {
    path: 'diagnostic',
    component: DiagnosticComponent,
    resolve: {
      SideBarResolver: SideBarResolver
    }
  },
  /* Lista allerte */
  {
    path: 'alertsHistory',
    component: AlertsHistoryPage,
    resolve: {
      SideBarResolver: SideBarResolver
    },
    canActivate: [organizationGuard]
  },
  {
    path: '',
    redirectTo: 'customers',
    pathMatch: 'full',
  }
];
